import { ReactNode, useState, useEffect } from "react";
import { NavLink } from "./NavLink";
import {
  Building2,
  LayoutDashboard,
  Home,
  Calculator,
  DollarSign,
  LogOut,
  User,
  FileText,
  Globe,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import AuthService from "@/services/APIs/AuthService";
import {
  clearLocalStorage,
  getTenantId,
  setLocalTenantId,
  getAccessToken,
} from "@/lib/storeManager";
import { toast } from "sonner";
import navigationRoutes from "@/project/propertyPortal/utils/navigationRoutes";

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const [userEmail, setUserEmail] = useState<string>("");

  useEffect(() => {
    // Get user email from localStorage
    const userInfo = localStorage.getItem("userInfo");
    if (userInfo) {
      try {
        const parsed = JSON.parse(userInfo);
        setUserEmail(parsed.email ?? "");
      } catch {
        setUserEmail("");
      }
    }
  }, []);

  const handleLogout = async () => {
    try {
      // Call logout API if token exists
      const token = getAccessToken();
      if (token) {
        try {
          await AuthService.logout();
        } catch (error) {
          // Even if logout API fails, continue with local cleanup
          console.error("Logout API error:", error);
        }
      }

      const preservedTenant = getTenantId();

      // Clear all local storage
      clearLocalStorage();

      // Restore tenant ID if it existed
      if (preservedTenant) {
        setLocalTenantId(preservedTenant);
      }

      toast.success("Logged out successfully");
      window.location.href = navigationRoutes.LOGIN;
    } catch (error) {
      console.error("Logout error:", error);
      const preservedTenant = getTenantId();
      clearLocalStorage();
      if (preservedTenant) {
        setLocalTenantId(preservedTenant);
      }
      window.location.href = navigationRoutes.LOGIN;
    }
  };

  const navItems = [
    {
      to: navigationRoutes.DASHBOARD,
      icon: LayoutDashboard,
      label: "Dashboard",
    },
    {
      to: navigationRoutes.PROPERTY_MASTER,
      icon: Home,
      label: "Property Master",
    },
    {
      to: navigationRoutes.COMPARISON_PROPERTY_MASTER,
      icon: Building2,
      label: "Comparison Property Master",
    },
    {
      to: navigationRoutes.REHAB_ESTIMATOR,
      icon: Calculator,
      label: "Rehab Estimator",
    },
    {
      to: navigationRoutes.MAX_BID_CALCULATION,
      icon: DollarSign,
      label: "Max Bid Calculation",
    },
    { to: navigationRoutes.REPORTS, icon: FileText, label: "Reports" },
    {
      to: navigationRoutes.MY_PROPERTY,
      icon: Building2,
      label: "My Property",
    },
    {
      to: navigationRoutes.IMPORTANT_WEBSITE,
      icon: Globe,
      label: "Important Websites",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 z-50 h-screen w-64 border-r border-border bg-card shadow-lg">
        <div className="border-b border-border p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-secondary">
              <Building2 className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-lg font-bold text-transparent">
                Property Portal
              </h1>
              <p className="text-xs text-muted-foreground">
                Investment Manager
              </p>
            </div>
          </div>
        </div>

        <nav className="space-y-2 p-4">
          {navItems.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className="flex items-center gap-3 rounded-lg px-4 py-3 text-muted-foreground transition-all duration-200 hover:bg-muted hover:text-foreground"
                activeClassName="bg-gradient-to-r from-primary/10 to-secondary/10 text-primary font-medium border-l-4 border-primary"
              >
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        {/* Top Header with My Profile */}
        <header className="sticky top-0 z-40 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
          <div className="flex h-16 items-center justify-end px-8">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  <span className="hidden sm:inline-block">My Profile</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      My Profile
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {userEmail || "Not logged in"}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div className="p-5 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
